import { IBackgroundData, IBackgroundStyle, IImageBackgroundData, IMediaBackgroundStyle, IVideoBackgroundData } from "@pageBuilder/typings";
import { CssProperties } from "../../models/css-property";
import { ElementStyleService } from "../../services/element-style.service";

export class BackgroundDataRenderer {

    constructor(private readonly styleService: ElementStyleService) {
    }

    public renderBackground(element: HTMLElement, data: IBackgroundData, selectors?: string, defaultAlt?: string): void {
        this.cleanupMediaBackground(element, selectors);

        if (data?.mediaBackground) {
            if (data.mediaBackground.type === "image") {
                this.renderImageBackground(element, data.mediaBackground, selectors, defaultAlt);
            } else if (data.mediaBackground.type === "video") {
                this.renderVideoBackground(element, data.mediaBackground, selectors);
            }
        }

        this.renderBackgroundStyles(element, data?.style, selectors);
    }

    public getContentSelectorsInternal(element: HTMLElement, selectors?: string): string[] {
        const selectorsArray = new Array<string>();
        selectorsArray.push(this.getBackgroundSelector(element, selectors));
        selectorsArray.push(this.getImageSelector(element, selectors));
        selectorsArray.push(this.getVideoSelector(element, selectors));

        return selectorsArray;
    }

    private getBackgroundSelector(element: HTMLElement, selectors?: string): string {
        return this.getBaseSelector(element, selectors);
    }

    public cleanupBackgroundStyles(element: HTMLElement, selectors?: string): void {
        this.styleService.clearStyle(element, this.getBackgroundSelector(element, selectors));
    }

    public cleanupMediaBackgroundContent(element: HTMLElement, selectors?: string): void {
        const background = element.querySelector<HTMLElement>(`${selectors ?? ".background"}`);
        if (background) {
            background.innerHTML = "";
        }
    }

    public cleanupMediaBackground(element: HTMLElement, selectors?: string): void {
        this.cleanupMediaBackgroundContent(element, selectors);
        this.cleanupImageBackgroundStyles(element, selectors);
        this.cleanupVideoBackgroundStyles(element, selectors);
    }

    protected renderImageBackground(element: HTMLElement, backgroundData: IImageBackgroundData, selectors?: string, defaultAlt?: string): void {
        this.renderImageBackgroundInternal(element, backgroundData, selectors, defaultAlt);
        this.renderImageBackgroundStyles(element, backgroundData.style, selectors);
    }

    protected renderImageBackgroundInternal(element: HTMLElement, backgroundData: IImageBackgroundData, selectors?: string, defaultAlt?: string): void {
        const backgroundElement = this.ensureBackgroundElement(element, selectors);
        let image = backgroundElement?.querySelector("img");
        if (!image) {
            image = document.createElement("img");
            image.classList.add("background-image");
            backgroundElement.appendChild(image);
        }

        if (image.src !== backgroundData.src) {
            image.src = backgroundData.src ?? "";
        }

        if (image.alt !== backgroundData.altText) {
            image.alt = backgroundData.altText ?? defaultAlt ?? "";
        }
    }

    protected renderImageBackgroundStyles(element: HTMLElement, style?: IMediaBackgroundStyle | null, selectors?: string): void {
        if (!style) {
            this.cleanupImageBackgroundStyles(element, selectors);
            return;
        }

        const imageProps = new CssProperties();

        if (style.position) {
            imageProps.tryAdd("object-position", `${style.position.offsetX}% ${style.position.offsetY}%`);
        }

        imageProps.tryAdd("opacity", `${style.opacity ?? 1}`);
        this.styleService.setStyle(element, `${this.getBaseSelector(element, selectors)} img`, imageProps.data);
    }

    protected renderVideoBackground(element: HTMLElement, backgroundData: IVideoBackgroundData, selectors?: string): void {
        this.renderVideoBackgroundInternal(element, backgroundData, selectors);
        this.renderVideoBackgroundStyles(element, backgroundData.style, selectors);
    }

    protected renderVideoBackgroundInternal(element: HTMLElement, backgroundData: IVideoBackgroundData, selectors?: string): void {
        const backgroundElement = this.ensureBackgroundElement(element, selectors);
        let video = backgroundElement?.querySelector("video");
        if (!video) {
            video = document.createElement("video");
            backgroundElement.appendChild(video);
        }

        if (video.src !== backgroundData.src) {
            video.src = backgroundData.src ?? "";
        }
        video.muted = true;
        video.controls = false;
        video.playsInline = true;
        video.setAttribute("autoplay", "");
        video.setAttribute("loop", "true");
        video.setAttribute("muted", "true");
        video.poster = `${backgroundData.thumbnailUrl ?? ""}`;
    }

    protected renderVideoBackgroundStyles(element: HTMLElement, style?: IMediaBackgroundStyle | null, selectors?: string): void {
        if (!style) {
            this.cleanupVideoBackgroundStyles(element, selectors);
            return;
        }

        const videoProps = new CssProperties();
        videoProps.tryAdd("opacity", `${style.opacity ?? 1}`);
        this.styleService.setStyle(element, `${this.getBaseSelector(element, selectors)} video`, videoProps.data);
    }

    private ensureBackgroundElement(element: HTMLElement, selectors?: string): HTMLElement {
        let background = element.querySelector<HTMLElement>(`${selectors ?? ".background"}`);
        if (!background) {
            background = document.createElement("div");
            background.classList.add("background");
            if (selectors) { // ToDo refactoring
                const contentElement = element.querySelector<HTMLElement>(".content");
                contentElement?.insertAdjacentElement("afterbegin", background);
            }
            else {
                element.insertAdjacentElement("afterbegin", background);
            }
        }

        return background;
    }

    protected renderBackgroundStyles(element: HTMLElement, style?: IBackgroundStyle | null, selectors?: string): void {
        if (!style) {
            this.cleanupBackgroundStyles(element, selectors);
            return;
        }

        this.ensureBackgroundElement(element, selectors);
        const backgroundProps = new CssProperties();
        backgroundProps.tryAdd("background-color", style.color);
        this.styleService.setStyle(element, this.getBaseSelector(element, selectors), backgroundProps.data);
    }

    protected cleanupImageBackgroundStyles(element: HTMLElement, selectors?: string): void {
        this.styleService.clearStyle(element, this.getImageSelector(element, selectors));
    }

    protected cleanupVideoBackgroundStyles(element: HTMLElement, selectors?: string): void {
        this.styleService.clearStyle(element, this.getVideoSelector(element, selectors));
    }

    private getImageSelector(element: HTMLElement, selectors?: string): string {
        return `${this.getBaseSelector(element, selectors)} img`;
    }

    private getVideoSelector(element: HTMLElement, selectors?: string): string {
        return `${this.getBaseSelector(element, selectors)} video`;
    }

    private getBaseSelector(element: HTMLElement, selectors?: string): string {
        const idSelector = element?.id ? `#${element.id} ` : "";
        return `${idSelector}${selectors ?? ".background"}`;
    }

}
